import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Calendar03Icon } from '@hugeicons/core-free-icons';
import { useAppSession } from '../hooks/useAppSession';
import {
  useScheduledScans,
  useUpdateScheduledScan,
  useDeleteScheduledScan,
  useRunScheduledScan,
  useReviewScheduledScanFindings,
} from '../hooks/useScheduledScans';
import { DataTable } from '../components/shared/DataTable';
import { ConfirmDialog } from '../components/shared/ConfirmDialog';
import { LoadError } from '../components/shared/IssuesLoadError';
import { EmptyState } from '../components/ui/EmptyState';
import { NewScheduleModal } from '../components/schedule/NewScheduleModal';
import { buildScheduleColumns, buildScheduleRowActions } from '../components/schedule/scheduleColumns';
import { ScheduleStatsBand } from '../components/schedule/ScheduleStatsBand';
import { useCreateLiveReview } from '../hooks/useClientReviews';
import { buildCreatedReviewUrl } from '../lib/createdReviewUrl';
import { openCreatedReview } from '../lib/openCreatedReview';
import { useExtensionDetection } from '../hooks/useExtensionDetection';
import { useProjects } from '../hooks/useProjects';
import { writePendingExtensionOpen } from '../lib/pendingExtensionOpen';
import { CHROME_EXTENSION_URL } from '../lib/constants';
import type { CadenceValue, ScheduledScanRow } from '../lib/api/scheduledScans';
import { ApiError } from '../lib/api/apiError';
import { capture } from '../lib/analytics';
import { toast } from 'sonner';
import { toUserFacingMessage } from '@overlayqa/shared';

/**
 * A sentence to show the user when a row mutation fails.
 *
 * Prefers the server's own reason (ApiError carries it, and the API client
 * has already unwrapped the envelope and the zValidator shape by this point),
 * and falls back to something readable rather than an empty toast or a
 * stringified object. An empty string is treated as absent: `new Error('')`
 * is a real shape a rejected fetch can produce, and a blank toast is the same
 * silence this exists to end.
 */
/**
 * The site a toast is about, in the shortest form that is still the user's own
 * string. Mirrors hostForMessage in routes/scheduledScans.ts so a success
 * toast and the server's failure sentence name the site the same way.
 */
function hostLabel(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
}

/**
 * What the user is told when the row they acted on no longer exists.
 *
 * NOT the server's own sentence. `scheduleErrorMessage` deliberately prefers
 * the server's reason, and for a 404 that reason is "Schedule not found" — a
 * status line, shown to somebody looking straight at the row it claims does
 * not exist. This is the one case where the server's wording is the wrong
 * wording, so it is replaced rather than quoted, and it says what the product
 * just did about it.
 */
const GONE_MESSAGE =
  'That schedule was already deleted, so the list has been refreshed.';

function isGone(err: unknown): boolean {
  return err instanceof ApiError && err.status === 404;
}

function scheduleErrorMessage(err: unknown): string {
  if (isGone(err)) return GONE_MESSAGE;
  // Curated server copy passes through; an empty, placeholder or internal
  // message ("Failed to fetch", "Unexpected token") falls back.
  return toUserFacingMessage(err, 'Something went wrong and the change was not saved. Please try again.');
}

export function SchedulePage() {
  // Fire the page-view once on mount (mirrors billing_page_viewed).
  const viewedRef = useRef(false);
  useEffect(() => {
    if (viewedRef.current) return;
    viewedRef.current = true;
    capture('schedule_page_viewed');
  }, []);

  const { data: appSession } = useAppSession();
  const activeTeamId = appSession?.activeTeamId;

  const { data: schedules, isLoading, error, refetch } = useScheduledScans();
  const updateSchedule = useUpdateScheduledScan();
  const deleteSchedule = useDeleteScheduledScan();
  const runSchedule = useRunScheduledScan();
  const reviewFindings = useReviewScheduledScanFindings();
  const createLiveReview = useCreateLiveReview();
  // To stamp `ext=1` on the created review URL, exactly as the modal does, and
  // to pick the branch of "Scan with the extension" below.
  const { detected: extensionDetected } = useExtensionDetection();
  // The schedule list spans all accessible teams. Resolve the same projects
  // for permissions and extension handoffs, including archived projects whose
  // schedules an owner must still be able to stop.
  const { data: projects } = useProjects(undefined, { allTeams: true, includeArchived: true });

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<{ id: string; url: string } | null>(null);
  /**
   * Schedules whose team-review create is in flight, by id.
   *
   * A SET, and a ref beside it, for exactly the reasons handleRunNow documents
   * below: one boolean cannot say WHICH row is busy, and state alone cannot
   * guard re-entry because setState is async and the disabled attribute has not
   * rendered yet when a second click lands in the same React batch.
   */
  const [capturingIds, setCapturingIds] = useState<ReadonlySet<string>>(new Set());
  const capturingIdsRef = useRef<Set<string>>(new Set());
  /**
   * Schedules whose manual run is in flight, by id.
   *
   * A SET rather than `runSchedule.isPending`, which is one boolean for the
   * whole mutation and so cannot say WHICH row is busy. That distinction is the
   * feature: the busy treatment belongs on the row being scanned, and a table
   * of independently watched pages must stay usable while one of them runs.
   */
  /**
   * Schedules whose findings-review create is in flight, by id. Same SET plus
   * REF shape as capturingIds and runningIds, and for the same two reasons:
   * one boolean cannot say WHICH row is busy, and state alone cannot guard
   * re-entry because setState is async and the disabled attribute has not
   * rendered when a second click lands in the same React batch.
   */
  const [reviewingIds, setReviewingIds] = useState<ReadonlySet<string>>(new Set());
  const reviewingIdsRef = useRef<Set<string>>(new Set());
  const [runningIds, setRunningIds] = useState<ReadonlySet<string>>(new Set());
  /**
   * The same set, as the SYNCHRONOUS authority for the re-entry guard.
   *
   * State alone cannot guard re-entry: `handleRunNow` closes over `runningIds`
   * and `setRunningIds` is async, so two invocations inside one React batch both
   * read the set as it was at render time (empty) and both proceed. The disabled
   * attribute does not cover that window either, because the DOM has not
   * re-rendered yet. Proven, not theorised: the test "two invocations inside one
   * batch still fire only one scan" fired TWO before this ref existed, and the
   * unit of waste here is a real Puppeteer browser.
   *
   * The ref is the gate; the state is only what renders.
   */
  const runningIdsRef = useRef<Set<string>>(new Set());

  /**
   * A mutation failed. Tell the user, and when the row is GONE, drop it.
   *
   * The refetch is the half that was missing: useApiMutation invalidates on
   * success only, so a row a teammate had already deleted survived its own 404
   * and stayed clickable, leaving a phantom the user could only clear by
   * reloading (reproduced twice, 2026-08-17).
   */
  const reportFailure = useCallback(
    (err: unknown) => {
      toast.error(scheduleErrorMessage(err));
      if (isGone(err)) void refetch();
    },
    [refetch],
  );

  // Surface every failure. The mutation layer does NOT do this for us, despite
  // what the comment here used to claim: useScheduledScans passes no onError,
  // and useApiMutation only wires one when supplied. Only a 403 was ever
  // visible, via the global interceptor's upgrade modal, so a 404 (a teammate
  // deleted the row concurrently), a 409, any 5xx or a dropped connection was
  // silent. NewScheduleModal already gets this right and says why in its own
  // docstring; these four row actions now match it.
  const runUpdate = useCallback(
    (id: string, data: Parameters<typeof updateSchedule.mutateAsync>[0]['data']) => {
      updateSchedule.mutateAsync({ id, data }).catch(reportFailure);
    },
    [updateSchedule, reportFailure],
  );

  const handleCadenceChange = useCallback(
    (schedule: ScheduledScanRow, cadence: CadenceValue) => {
      // Picking the cadence a schedule already has is a no-op, not a PATCH:
      // the server fires scheduled_scan_updated {action: cadence_change} on
      // every accepted cadence write, so a re-pick would log a change that
      // never happened.
      if (schedule.cadence === cadence) return;
      runUpdate(schedule.id, { cadence });
    },
    [runUpdate],
  );

  /**
   * A successful run is CONFIRMED, unlike the other row actions.
   *
   * Pause and cadence changes rewrite a visible cell, so the table is its own
   * receipt. A run that finds exactly what the last one found rewrites only
   * lastScannedAt, which this table does not render, so with no toast the
   * button reads as broken. The failure path matters even more: learning why
   * nothing is happening is the reason the button exists, so the server's own
   * sentence is surfaced verbatim.
   */
  const handleRunNow = useCallback(
    (schedule: ScheduledScanRow) => {
      // Re-entry guard, not just a disabled button. The menu item is disabled
      // from `runningIds` too, but THIS is the authority: a stale menu, a
      // double-fire, or a keyboard repeat must not buy a second browser. Read
      // and written through the ref so the check and the claim are in the same
      // tick; see runningIdsRef.
      if (runningIdsRef.current.has(schedule.id)) return;
      runningIdsRef.current.add(schedule.id);
      setRunningIds(new Set(runningIdsRef.current));
      runSchedule
        .mutateAsync(schedule.id)
        .then(() => {
          toast.success(`Scan finished for ${hostLabel(schedule.url)}.`);
        })
        .catch(reportFailure)
        .finally(() => {
          // finally, not then: a FAILED run must release the row too, or the
          // one schedule the user is trying to diagnose is the one left
          // permanently stuck reading "Scanning...".
          runningIdsRef.current.delete(schedule.id);
          setRunningIds(new Set(runningIdsRef.current));
        });
    },
    // No `runningIds` here on purpose: the guard reads the ref, so this callback
    // does not need to be rebuilt on every start and stop of a run.
    [runSchedule, reportFailure],
  );

  /**
   * Capture Issues: create a team review for this row's page and open it.
   *
   * NO CONFIRM STEP. This used to open the shared LiveReviewModal prefilled
   * with the row's URL, so the user confirmed a URL the row already held. The
   * modal's pending/cancel handling was justified by a 60-100s create, but a
   * real-browser measurement puts click-to-usable at ~1.2-1.6s
   * (docs/test-reports/2026-08-22-schedule-review-findings-gap/), so the
   * confirm was buying a click rather than a chance to react.
   *
   * The popup-block fallback the modal owned is NOT lost: openCreatedReview
   * still handles it, navigating this tab when window.open is refused (the
   * 2026-08-03 dead-click incident). `closedByUser` is false here because
   * there is no surface for the user to have closed.
   *
   * A streamed (Steel) session degrades to the normal review page rather than
   * the full-screen InteractiveReview overlay, which needs a host component a
   * table row does not have. That path is SPA-only and behind
   * ENABLE_STREAMED_TEAM_REVIEW, which is off in production.
   */
  /**
   * Hand a bot-walled schedule's page to the extension (2026-09-22). The
   * site's bot protection refuses our scanner; the user's own browser is let
   * in. Reuses the onboarding handoff: with the extension detected, post
   * OVERLAYQA_OPEN_SITE (the content script relays it and the background
   * opens the page with the sidebar on this project); without it, stash the
   * URL for the install-and-return flow and open the store.
   */
  const handleScanWithExtension = useCallback(
    (schedule: ScheduledScanRow) => {
      const project = (projects ?? []).find((p) => p.id === schedule.projectId);
      const payload = {
        url: schedule.url,
        urlType: 'scheduled_scan',
        projectId: schedule.projectId,
        projectName: project?.name ?? schedule.projectName ?? undefined,
        projectSlug: project?.slug,
        teamId: project?.teamId ?? activeTeamId,
        userId: appSession?.user.id,
      };
      capture('schedule_scan_with_extension_clicked', {
        schedule_id: schedule.id,
        status: schedule.status,
        extension_detected: extensionDetected,
      });
      if (extensionDetected) {
        window.postMessage({ type: 'OVERLAYQA_OPEN_SITE', payload }, window.location.origin);
        return;
      }
      writePendingExtensionOpen(payload);
      window.open(CHROME_EXTENSION_URL, '_blank', 'noopener,noreferrer');
    },
    [projects, activeTeamId, extensionDetected, appSession?.user.id],
  );

  const handleCaptureIssues = useCallback(
    (schedule: ScheduledScanRow) => {
      // The ref is the gate, not the disabled attribute: see runningIdsRef.
      if (capturingIdsRef.current.has(schedule.id)) return;
      capturingIdsRef.current.add(schedule.id);
      setCapturingIds(new Set(capturingIdsRef.current));
      // The action was unmeasurable before this: it carried no capture() while
      // every sibling row action had one, so nobody could tell whether it was
      // ever clicked.
      capture('schedule_capture_issues_clicked', {
        project_id: schedule.projectId,
        scan_type: schedule.scanType,
        has_findings: schedule.lastFindingCount > 0,
      });
      createLiveReview
        .mutateAsync({ projectId: schedule.projectId, pageUrl: schedule.url })
        .then((data) => {
          const fullUrl = buildCreatedReviewUrl({
            shareUrl: data?.shareUrl,
            liveAvailable: data?.liveAvailable,
            extensionDetected,
            authHandoff: data?.authHandoff,
            // A schedule watches ONE scan type, so land on that workflow rather
            // than on the issues list three clicks away from it.
            openWorkflow: schedule.scanType === 'auto_review' ? 'auto-review' : schedule.scanType === 'tokens' ? 'design-tokens' : 'accessibility-audit',
          });
          if (!fullUrl) {
            // A 201 with no shareUrl is a server-side surprise, not a user
            // error. Say so rather than navigating to "null".
            toast.error(`The review for ${hostLabel(schedule.url)} was created but did not come back with a link.`);
            return;
          }
          openCreatedReview(fullUrl, false);
        })
        .catch(reportFailure)
        .finally(() => {
          // finally, not then: a FAILED create must release the row too, or the
          // one schedule the user is trying to diagnose is the one left stuck.
          capturingIdsRef.current.delete(schedule.id);
          setCapturingIds(new Set(capturingIdsRef.current));
        });
    },
    // No `capturingIds`: the guard reads the ref, so this does not need
    // rebuilding on every start and stop.
    [createLiveReview, extensionDetected, reportFailure],
  );

  /**
   * Review findings: open a team review on the scan's OWN canvas.
   *
   * The whole point is that it captures NOTHING. The scan already stored the
   * DOM it measured its findings in, so this points a review at those bytes
   * and the findings pin where they actually happened. A fresh capture would
   * be a different render and the pins would land on the wrong elements.
   *
   * Same popup handling as Capture Issues: openCreatedReview navigates this
   * tab when window.open is refused (the 2026-08-03 dead-click incident).
   */
  const handleReviewFindings = useCallback(
    // `entry` names the door: the Findings column's "View" or the row menu's
    // "Review findings". Both open the same review; the event tells them
    // apart so the column (added 2026-09-09) can be read against the menu
    // rather than pooled with it.
    (schedule: ScheduledScanRow, entry: 'column' | 'menu') => {
      // The ref is the gate, not the disabled attribute: see reviewingIdsRef.
      if (reviewingIdsRef.current.has(schedule.id)) return;
      reviewingIdsRef.current.add(schedule.id);
      setReviewingIds(new Set(reviewingIdsRef.current));
      capture('schedule_review_findings_clicked', {
        project_id: schedule.projectId,
        scan_type: schedule.scanType,
        finding_count: schedule.lastFindingCount,
        entry,
      });
      reviewFindings
        .mutateAsync(schedule.id)
        .then((data) => {
          const fullUrl = buildCreatedReviewUrl({
            shareUrl: data?.shareUrl,
            liveAvailable: data?.liveAvailable,
            extensionDetected,
            authHandoff: data?.authHandoff,
            // Land on the panel the findings are in, not three clicks away.
            openWorkflow: schedule.scanType === 'auto_review' ? 'auto-review' : schedule.scanType === 'tokens' ? 'design-tokens' : 'accessibility-audit',
          });
          if (!fullUrl) {
            toast.error(`The review for ${hostLabel(schedule.url)} was created but did not come back with a link.`);
            return;
          }
          openCreatedReview(fullUrl, false);
        })
        .catch(reportFailure)
        .finally(() => {
          // finally, not then: a FAILED create must release the row too, or
          // the one schedule being diagnosed is the one left stuck.
          reviewingIdsRef.current.delete(schedule.id);
          setReviewingIds(new Set(reviewingIdsRef.current));
        });
    },
    [reviewFindings, extensionDetected, reportFailure],
  );

  const handleDelete = useCallback(async (id: string) => {
    try {
      await deleteSchedule.mutateAsync(id);
    } catch (err: unknown) {
      // The dialog closes either way (below, in the finally), so without this
      // a failed delete was indistinguishable from a successful one: the row
      // simply reappeared on the next refetch.
      reportFailure(err);
    } finally {
      setConfirmDelete(null);
    }
  }, [deleteSchedule, reportFailure]);

  // One clock for the whole render pass, so every "in N days" cell agrees, but
  // read fresh on EVERY render rather than memoized. Memoizing it against
  // `schedules` looked cheaper and was wrong: TanStack hands back the same
  // array reference from cache, so a re-render hours later kept rendering the
  // relative label computed at first paint ("in 6 hrs" long after it was 2).
  // Rebuilding the column array per render is a handful of object literals.
  //
  // The stats band reads the SAME clock rather than its own. Both judge
  // overdue-ness through scheduleRowCopy.isRunOverdue, so two clocks a
  // millisecond apart could put a row past the grace window in one and not the
  // other, and the summary would contradict the row it summarises.
  const now = new Date();
  const columns = buildScheduleColumns(now, runningIds, {
    onReviewFindings: (schedule) => handleReviewFindings(schedule, 'column'),
    reviewingIds,
  });

  const rowActions = useMemo(
    () =>
      buildScheduleRowActions({
        canManage: (schedule) => {
          const role = projects?.find(project => project.id === schedule.projectId)?.role;
          return role === 'owner' || role === 'collaborator' || role === 'team';
        },
        onCaptureIssues: handleCaptureIssues,
        onReviewFindings: (schedule) => handleReviewFindings(schedule, 'menu'),
        onRunNow: handleRunNow,
        onScanWithExtension: handleScanWithExtension,
        onPause: (schedule) => runUpdate(schedule.id, { action: 'pause' }),
        onResume: (schedule) => runUpdate(schedule.id, { action: 'resume' }),
        onCadenceChange: handleCadenceChange,
        setConfirmDelete,
        runningIds,
        capturingIds,
        reviewingIds,
      }),
    [projects, runUpdate, handleCadenceChange, handleRunNow, handleScanWithExtension, handleCaptureIssues, handleReviewFindings, runningIds, capturingIds, reviewingIds],
  );

  if (error) {
    return (
      <section id="schedule" className="page-content">
        {/* Form measure, not --wide: this branch is prose, and the reading
            measure is what caps a line at 45-75 characters. Matches
            ProjectsPage's load-error branch, which already did this. */}
        <div className="page-content__inner">
          <LoadError resource="scheduled scans" error={error} onRetry={() => refetch()} />
        </div>
      </section>
    );
  }

  const newScheduleButton = (
    <button
      className="btn-primary btn-toolbar gap-2 whitespace-nowrap"
      onClick={() => setShowCreateModal(true)}
      disabled={!activeTeamId}
    >
      {/* The same plus glyph as Projects' "New project" (ProjectsPage.tsx); a typed "+" is not an icon. */}
      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2} strokeLinecap="round" aria-hidden="true"><path d="M12 4v16m8-8H4" /></svg>
      <span className="text-cap-trim">New schedule</span>
    </button>
  );

  return (
    <section id="schedule" className="page-content">
      {/* Actions row only. This page deliberately renders NO heading of its
          own: the top nav already shows "Schedule" (Layout.tsx PageTitle),
          and the design system's page scaffold is one title per page, in the
          nav ("no repeated title", DesignSystemPage.tsx page-scaffold). This
          was the only page in the app that rendered a second one, so the nav
          read "Schedule" and the body read "Scheduled Scans" beneath it.
          The description that sat here went with it: no sibling page carries
          one, and the empty state already explains the feature verbatim to
          the only user who has nothing else to read. Shape mirrors
          ProjectsPage's toolbar, with the action pushed right. */}
      <header className="list-toolbar">
        <div className="list-toolbar__actions lg:col-start-2">{newScheduleButton}</div>
      </header>

      {isLoading && !schedules ? (
        <p className="text-sm text-gray-500">Loading scheduled scans...</p>
      ) : (
        <div className="data-fade-in">
          {/* Above the table, because the table cannot answer "did anything
              change, is anything broken" without being read row by row, and
              on prod there are 65 of them. Renders nothing when the list is
              empty so the EmptyState keeps the floor. */}
          <div className="mb-4">
            <ScheduleStatsBand schedules={schedules ?? []} now={now} />
          </div>
          <DataTable<ScheduledScanRow>
            columns={columns}
            data={schedules ?? []}
            rowKey={(schedule) => schedule.id}
            rowActions={rowActions}
            selectable={false}
            rowLabel={(schedule) => schedule.url}
            loading={updateSchedule.isPending || deleteSchedule.isPending}
            emptyState={
              <EmptyState
                icon={Calendar03Icon}
                title="No scheduled scans yet"
                hint="Watch a page and we'll email you the moment something breaks. No reminder to check needed."
                action={newScheduleButton}
              />
            }
          />
        </div>
      )}

      <ConfirmDialog
        open={confirmDelete !== null}
        title="Delete schedule"
        message={
          confirmDelete
            ? `Delete the scheduled scan for ${confirmDelete.url}? Future scans stop and its baseline is removed. This cannot be undone.`
            : undefined
        }
        confirmLabel="Delete"
        onConfirm={() => confirmDelete && handleDelete(confirmDelete.id)}
        onCancel={() => setConfirmDelete(null)}
        destructive
        loading={deleteSchedule.isPending}
      />

      {showCreateModal && activeTeamId && (
        <NewScheduleModal
          activeTeamId={activeTeamId}
          onClose={() => setShowCreateModal(false)}
        />
      )}

    </section>
  );
}
